// B1.4: move-only from client.ts.
import { ApiClientCore, buildQueryString } from './http/core';

export class SalaryPayrollApi extends ApiClientCore {
  async calculatePayroll(id: number): Promise<any> {
    return this.post(`/api/v1/payroll/${id}/calculate`);
  }
  async createPayroll(data: any): Promise<any> {
    return this.post('/api/v1/payroll', data);
  }
  async createSalaryRate(data: any): Promise<any> {
    return this.post('/api/v1/salary-rates', data);
  }
  async deletePayroll(id: number): Promise<any> {
    return this.delete(`/api/v1/payroll/${id}`);
  }
  // ─── Chat ───────────────────────────────────────────────────────────────────
  async deleteSalaryRate(id: number): Promise<any> {
    return this.delete(`/api/v1/salary-rates/${id}`);
  }
  // ─── Payroll ────────────────────────────────────────────────────────────────
  async getMyPayrollSummary(params: {
    period_start: string;
    period_end: string;
  }): Promise<{
    period_start: string;
    period_end: string;
    shifts_count: number;
    hours: number | string;
    total_amount: number | string;
    amount_note?: string | null;
    source: string;
  }> {
    return this.get(`/api/v1/payroll/my-summary${buildQueryString(params)}`);
  }
  async getMyPayrollSummaryDaily(params: {
    period_start: string;
    period_end: string;
  }): Promise<{
    period_start: string;
    period_end: string;
    rows: Array<{
      date: string;
      hours: number | string;
      shifts_count?: number;
      amount_estimate?: number | string | null;
      rate_type?: string | null;
      rate_amount?: number | string | null;
      day_kind?: 'worked' | 'planned';
      status_label?: string | null;
      rate_display?: string | null;
    }>;
  }> {
    return this.get(`/api/v1/payroll/my-summary/daily${buildQueryString(params)}`);
  }
  async getPayroll(id: number): Promise<any> {
    return this.get(`/api/v1/payroll/${id}`);
  }
  async getPayrollItems(id: number, params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/payroll/${id}/items${buildQueryString(params)}`);
  }
  async getPayrollPresets(year: number, month: number): Promise<{
    year: number;
    month: number;
    presets: Array<{
      accrual_kind: string;
      period_start: string;
      period_end: string;
      payout_window_start: string;
      payout_window_end: string;
      label: string;
    }>;
  }> {
    return this.get(`/api/v1/payroll/presets${buildQueryString({ year, month })}`);
  }
  async getPayrollStats(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/payroll/stats${buildQueryString(params)}`);
  }
  async getPayrollTeamSummary(params: {
    period_start: string;
    period_end: string;
    location_id?: number;
  }): Promise<{
    period_start: string;
    period_end: string;
    location_id?: number | null;
    source: string;
    rows: Array<{
      employee_id: number;
      employee_name?: string | null;
      shifts_count: number;
      hours: number | string;
      rate_type?: string | null;
      rate_amount?: number | string | null;
      total_amount: number | string;
      multiple_rates?: boolean;
      amount_note?: string | null;
    }>;
    totals: {
      shifts_count: number;
      hours: number | string;
      total_amount: number | string;
    };
  }> {
    return this.get(`/api/v1/payroll/team-summary${buildQueryString(params)}`);
  }
  async getPayrolls(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/payroll${buildQueryString(params)}`);
  }
  async getSalaryRates(params: Record<string, any> = {}): Promise<any> {
    return this.get(`/api/v1/salary-rates${buildQueryString(params)}`);
  }
  async getSalaryRatesStats(): Promise<any> {
    return this.get('/api/v1/salary-rates/stats');
  }
  async regenerateWalletEnvelopeQr(id: number): Promise<any> {
    return this.post(`/api/v1/wallet/envelopes/${id}/qr/regenerate`);
  }
  async updatePayroll(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/payroll/${id}`, data);
  }
  async updatePayrollItem(payrollId: number, itemId: number, data: any): Promise<any> {
    return this.put(`/api/v1/payroll/${payrollId}/items/${itemId}`, data);
  }
  async updateSalaryRate(id: number, data: any): Promise<any> {
    return this.put(`/api/v1/salary-rates/${id}`, data);
  }
  async getEmployeeSalaryHistory(employeeId: number): Promise<any> {
    return this.get(`/api/v1/salary-rates/employee/${employeeId}/history`);
  }
  async getEmployeeSalaryRateHistory(employeeId: number): Promise<any> {
    return this.get(`/api/v1/salary-rates/employee/${employeeId}/history`);
  }
  async getPayrollTeamSummaryEmployeeDaily(
    employeeId: number,
    params: {
      period_start: string;
      period_end: string;
      location_id?: number;
    }
  ): Promise<{
    employee_id: number;
    period_start: string;
    period_end: string;
    location_id?: number | null;
    rows: Array<{
      date: string;
      hours: number | string;
      shifts_count: number;
      amount_estimate?: number | string | null;
      rate_type?: string | null;
      rate_amount?: number | string | null;
    }>;
  }> {
    return this.get(
      `/api/v1/payroll/team-summary/employees/${employeeId}/daily${buildQueryString(params)}`
    );
  }
  async getEmployeeOpenAccrual(employeeId: number): Promise<{
    total_amount: number | string;
    period_start: string;
    period_end: string;
    payroll_status: string;
  } | null> {
    return this.get(`/api/v1/payroll/employees/${employeeId}/open-accrual`);
  }
}
